import { BarChartOutlined, WarningFilled } from '@ant-design/icons';
import { PageController } from '@client/components/_core/layout/PageController';
import { PageLayout } from '@client/components/_core/layout/PageLayout';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { ScopedRegisterEmptyState } from '@client/components/_shared/ScopedRegisterEmptyState';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Alerts } from '@client/components/_ui/display/Alerts';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { TaggedList } from '@client/components/_ui/display/TaggedList';
import { SourceTag } from '@client/components/_ui/display/tags';
import { User } from '@client/components/_ui/display/User';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Space } from '@client/components/_ui/layout/Space';
import { MissingComplaintProductTag } from '@client/components/complaints/MissingComplaintProductTag';
import { buildInitialWhereFromFilters, FilterBar } from '@client/components/filters';
import { isWhereClauseEmpty } from '@client/components/filters/core/where-builder';
import { TableViewsBar, useEntityBuiltInViews, useTableViews } from '@client/components/table-views';
import { ICONS } from '@client/constants/icons';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useHasRouteAccess } from '@client/hooks/navigation/useHasRouteAccess';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, useConfigurableFieldColumns, usePrismaTable } from '@client/hooks/table-v2';
import { useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { selectOrg, selectUser, useAuthStore } from '@client/stores/AuthStore';
import { queryClient, type RouterInputs, type RouterOutputs, trpc, trpcOptions } from '@client/trpc/client';
import {
    COMPLAINT_LIST_FIELD_CONFIGURATIONS_INPUT,
    DETAIL_LOOKUP_PREFETCH_OPTIONS,
    DETAIL_LOOKUP_QUERY_OPTIONS,
    LIST_ROUTE_PREFETCH_OPTIONS,
    LIST_ROUTE_QUERY_OPTIONS,
} from '@client/trpc/query-options';
import type { ShareableRouteContext } from '@client/types/router';
import { TokenRefreshManager } from '@client/utils/auth/TokenRefreshManager';
import { DEFAULT_MAX_ACCESSIBLE_RECORDS, DEFAULT_PAGE_SIZE, ORG_ROLES } from '@shared/constants';
import { FilterOperatorSchema, FilterValueSchema } from '@shared/schemas/filter';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Col, Row, Tag, Tooltip } from 'antd';
import clsx from 'clsx';
import { useState } from 'react';
import { z } from 'zod';
import { runDetailRoutePrefetchWhenAuthorised, shouldPrefetchDetailRouteData } from '../../detail-route-prefetch';
import {
    freshComplaintsListInput,
    freshComplaintsListInputWithWhere,
    LIST_TABLE_PERSIST_KEYS,
} from '../../fresh-list-inputs';
import { runListRoutePrefetch } from '../../list-route-prefetch';
import { getComplaintColumnReadiness, haveConfigurableColumnsReachedTable } from './complaint-column-readiness';
import { useComplaintFilterConfig } from './useComplaintFilterConfig';

export const Route = createFileRoute('/console/$orgId/compliance/complaints/')({
    staticData: {
        ui: {
            title: 'Complaints',
            subtitle: 'Create, track, and manage complaints and their resolution status',
            widthVariant: 'fullWidth',
            icon: ICONS.complaint,
        },
    },
    validateSearch: zodValidator(
        z.object({
            'complaint-filters': z
                .array(
                    z.union([
                        z.object({
                            id: z.string(),
                            filterId: z.literal('owner-ids'),
                            operator: FilterOperatorSchema,
                            value: z.union([z.array(z.coerce.bigint()), z.coerce.bigint()]),
                        }),
                        z.object({
                            id: z.string(),
                            filterId: z.string(),
                            operator: FilterOperatorSchema,
                            value: FilterValueSchema,
                        }),
                    ]),
                )
                .optional()
                .catch(undefined),
            view: z.string().optional().catch(undefined),
        }),
    ),
    beforeLoad: ({ context, params, search, location, preload, abortController }): ShareableRouteContext => {
        // Warm the saved-views round trip and (when the input is exactly computable)
        // the list query itself while the auth gate renders — see the claims list
        // route and `list-route-prefetch.ts` (CUR-1036, task 2.3).
        runDetailRoutePrefetchWhenAuthorised(
            () => {
                const authState = useAuthStore.getState();
                const user = selectUser(authState);
                return shouldPrefetchDetailRouteData({
                    hasValidToken: authState.hasValidToken({
                        minValidityMs: TokenRefreshManager.REFRESH_BUFFER_MS,
                    }),
                    userRoles: user?.roles,
                    allowedRoles: [ORG_ROLES.USER, ORG_ROLES.SHARED_EDIT_USER, ORG_ROLES.ORG_ADMIN],
                    hydratedOrg: selectOrg(authState),
                    routeOrgId: params.orgId,
                    requiredModule: 'compliance',
                });
            },
            () => {
                runListRoutePrefetch<RouterInputs['complaint']['listComplaints'], 'COMPLAINT'>({
                    savedViewsUtils: context.trpcUtils.savedView.listSavedViews,
                    entityType: 'COMPLAINT',
                    pathname: location.pathname,
                    urlFilterCount: search['complaint-filters']?.length ?? 0,
                    urlViewId: search.view,
                    defaultInput: freshComplaintsListInput(),
                    prefetchList: (input) => {
                        void context.trpcUtils.complaint.listComplaints.prefetch(input, LIST_ROUTE_PREFETCH_OPTIONS);
                    },
                });
                // The configurable columns wait for these before the views resolve (see
                // `complaint-column-readiness.ts`), so warm them in the same batch.
                void queryClient.prefetchQuery(
                    trpcOptions.fieldConfiguration.listFieldConfigurations.queryOptions(
                        COMPLAINT_LIST_FIELD_CONFIGURATIONS_INPUT,
                        DETAIL_LOOKUP_PREFETCH_OPTIONS,
                    ),
                );
            },
            { defer: !preload, signal: abortController.signal },
        );
        return undefined;
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ComplaintsSearch />
        </PageController>
    );
}

type ComplaintRow = RouterOutputs['complaint']['listComplaints']['rows'][number];
type QueryParams = RouterInputs['complaint']['listComplaints'];

const columnHelper = createColumnHelper<ComplaintRow, QueryParams['orderBy']>();

function ComplaintsSearch() {
    const orgId = useOrgId();
    const enableAiIntake = useFeatureFlag('enable-ai-intake', false);
    const { formatDate, formatDateTime } = useFormatter();
    const navigate = useNavigate();
    const searchParams = Route.useSearch();
    const hasRouteAccess = useHasRouteAccess();

    const { config: filterConfig, ready: filterConfigReady } = useComplaintFilterConfig();

    const [queryParams, setQueryParams] = usePathState<QueryParams>(
        'query-params',
        freshComplaintsListInputWithWhere(
            buildInitialWhereFromFilters(filterConfig, searchParams['complaint-filters']),
        ),
    );

    // Postpone the list query until saved views resolve which view loads first, so we fetch once
    // with the resolved where/sort/pageSize instead of unfiltered-then-refiltered (set by onResolved).
    const [viewsReady, setViewsReady] = useState(false);

    const { data, isLoading, isSuccess, error, refetch } = trpc.complaint.listComplaints.useQuery(queryParams, {
        enabled: filterConfigReady && viewsReady,
        ...LIST_ROUTE_QUERY_OPTIONS,
    });

    const fieldConfigurationsQuery = trpc.fieldConfiguration.listFieldConfigurations.useQuery(
        COMPLAINT_LIST_FIELD_CONFIGURATIONS_INPUT,
        DETAIL_LOOKUP_QUERY_OPTIONS,
    );
    // Hold the column controls and the views until the configurable columns exist, so neither
    // saves a layout without them (CUR-2041).
    const { columnControlsReady, viewsConfigReady } = getComplaintColumnReadiness(fieldConfigurationsQuery);

    const configurableColumns = useConfigurableFieldColumns<ComplaintRow>({
        fieldConfigurations: fieldConfigurationsQuery.data?.rows || [],
        getCustomFieldsData: (row) => row.customFieldsData,
        columnKeyPrefix: 'complaint-field-',
        visibleByDefault: false,
        onRenderError: (error, fieldConfig, row) => {
            console.warn('Configurable field render error:', {
                error: error.message,
                fieldId: fieldConfig.id,
                fieldLabel: fieldConfig.label,
                complaintId: row.id,
            });
        },
    });

    const staticColumns = [
        columnHelper.display({
            id: 'indicator',
            header: '',
            cell: ({ row }) => (
                <Alerts
                    id={BigInt(row.id)}
                    entity="complaint"
                    indicatorColor={row.indicatorColor}
                    key={row.id.toString()}
                />
            ),
            width: 40,
            fixed: 'left',
            hideable: false,
        }),
        columnHelper.accessor('id', {
            id: 'id',
            header: 'Complaint#',
            sortable: 'id',
            fixed: 'left',
            hideable: false,
        }),
        columnHelper.display({
            id: 'owner',
            header: 'Owner',
            cell: ({ row }) => (
                <User userId={row.Owner?.userId} name={row.Owner?.fullName} photoUrl={row.Owner?.photoUrl} />
            ),
        }),
        columnHelper.display({
            id: 'createdBy',
            header: 'Created by',
            cell: ({ row }) => (
                <User
                    userId={row.CreatedBy?.userId}
                    name={row.CreatedBy?.fullName}
                    photoUrl={row.CreatedBy?.photoUrl}
                />
            ),
        }),
        columnHelper.display({
            id: 'complainant',
            header: 'Complainant',
            // Either name can be null (imported and intake-created complaints): a template literal
            // rendered those rows as "null null".
            cell: ({ row }) => [row.complainantFirstName, row.complainantLastName].filter(Boolean).join(' ') || '—',
        }),
        columnHelper.accessor('policyNo', {
            id: 'policyNo',
            header: 'Policy#',
            sortable: 'policyNo',
        }),
        columnHelper.accessor('issues', {
            id: 'issues',
            header: 'Issues',
            sortable: 'issues',
            cell: ({ row }) => (
                <TaggedList
                    items={row.issues as string[] | []}
                    accessor={(item) => ({ label: item, color: 'volcano' })}
                    displayLimit={2}
                    emptyText=""
                    tagProps={{
                        maxWidth: '150px',
                    }}
                />
            ),
        }),
        columnHelper.accessor('products', {
            id: 'products',
            header: 'Products',
            sortable: 'products',
            cell: ({ row }) => {
                const products = row.products;

                if (!products?.length) {
                    return <MissingComplaintProductTag />;
                }

                return (
                    <TaggedList
                        items={products}
                        accessor={(item) => ({ label: item, color: 'blue' })}
                        displayLimit={2}
                        emptyText=""
                        tagProps={{
                            maxWidth: '150px',
                        }}
                    />
                );
            },
        }),
        columnHelper.display({
            id: 'flags',
            header: 'Open flags',
            cell: ({ row }) => (
                <TaggedList
                    items={row.Flags || []}
                    accessor={(item) => ({ label: item.labelActive, color: 'orange' })}
                    displayLimit={2}
                    emptyText=""
                    tagProps={{
                        maxWidth: '150px',
                    }}
                />
            ),
            hideable: true,
        }),
        columnHelper.accessor('status', {
            id: 'status',
            header: 'Status',
            sortable: 'status',
            cell: ({ row }) => row.status && <StatusBadge status={row.status} module="complaint" />,
        }),
        columnHelper.display({
            id: 'aiBreaches',
            header: 'AI breaches',
            cell: ({ row }) =>
                row.unresolvedAiBreachCount > 0 ? (
                    <Tooltip
                        title={`${row.unresolvedAiBreachCount} unresolved AI-detected breach${row.unresolvedAiBreachCount > 1 ? 'es' : ''}`}
                    >
                        <span className="flex items-center gap-1 text-orange-500">
                            <WarningFilled />
                            <span>{row.unresolvedAiBreachCount}</span>
                        </span>
                    </Tooltip>
                ) : null,
            width: 100,
        }),
        columnHelper.accessor('outcomes', {
            id: 'outcomes',
            header: 'Outcomes',
            sortable: 'outcomes',
            cell: ({ row }) => (
                <TaggedList
                    items={row.outcomes as string[] | []}
                    accessor={(item) => ({
                        label: item,
                        color: clsx(item === 'Rejected' && 'red', item === 'Accepted' && 'green'),
                    })}
                    displayLimit={2}
                    emptyText=""
                    tagProps={{
                        maxWidth: '150px',
                    }}
                />
            ),
        }),
        columnHelper.accessor('referredByAfca', {
            id: 'referredByAfca',
            header: 'Referred by AFCA',
            sortable: 'referredByAfca',
            cell: ({ row }) => <Tag>{row.referredByAfca ? 'Yes' : 'No'}</Tag>,
        }),
        columnHelper.accessor('createdAt', {
            id: 'createdAt',
            header: 'Date registered',
            sortable: 'createdAt',
            cell: ({ row }) => formatDateTime(row.createdAt),
        }),
        columnHelper.accessor('dtReceived', {
            id: 'dtReceived',
            header: 'Received on',
            sortable: 'dtReceived',
            cell: ({ row }) => formatDate(row.dtReceived),
        }),
        columnHelper.display({
            id: 'claimNo',
            header: 'Claim#',
            cell: ({ row }) => row.claimId || row.externalClaimId,
        }),
        columnHelper.accessor('source', {
            id: 'source',
            header: 'Source',
            sortable: 'source',
            cell: ({ row }) => row.source && <SourceTag value={row.source} />,
        }),
        columnHelper.actions({
            cell: ({ row }) => (
                <Button.OpenInNewTab
                    preload={false}
                    to="/console/$orgId/compliance/complaints/details/$id/manage"
                    params={{ orgId, id: row.id }}
                    tooltip="Open in new tab"
                />
            ),
        }),
    ];

    const allColumns = [...staticColumns, ...configurableColumns];

    const defaultHiddenColumns = [
        'createdBy',
        'flags',
        'claimNo',
        'referredByAfca',
        ...configurableColumns.map((col) => col.key),
    ];

    const tableProps = usePrismaTable({
        data: data?.rows ?? [],
        total: data?.total || 0,
        loading: !viewsReady || isLoading,
        columns: allColumns,
        rowKey: 'id',
        queryParams: queryParams,
        onParamsChange: setQueryParams,
        pagination: { position: 'both', maxAccessibleRecords: DEFAULT_MAX_ACCESSIBLE_RECORDS },
        columnVisibility: {
            enabled: columnControlsReady,
            persistKey: LIST_TABLE_PERSIST_KEYS.complaints,
            enableReordering: true,
            defaultHidden: defaultHiddenColumns,
        },
        refreshable: { enabled: true, onRefresh: () => refetch() },
        emptyContent:
            isSuccess && data.total === 0 ? (
                <ScopedRegisterEmptyState
                    entity="complaints"
                    hasActiveFilters={!isWhereClauseEmpty(queryParams.where)}
                />
            ) : undefined,
        emptyState: { noRecordsTitle: 'No complaints yet' },
    });

    // Resolve the views a render after the configurable columns reach the table's column order.
    const haveColumnsReachedTable = haveConfigurableColumnsReachedTable(
        configurableColumns.map((col) => col.key),
        tableProps.columnOrder,
    );
    const builtInViews = useEntityBuiltInViews('owner-ids');
    const views = useTableViews({
        entityType: 'COMPLAINT',
        config: filterConfig,
        configReady: filterConfigReady && viewsConfigReady && haveColumnsReachedTable,
        urlFilterKey: 'complaint-filters',
        builtInViews,
        initialFilters: searchParams['complaint-filters'],
        initialActiveViewId: searchParams.view,
        queryParams,
        setQueryParams,
        table: tableProps,
        columnPersistKey: 'complaints-table-columns-with-configurable-fields',
        defaultOrderBy: { id: 'desc' },
        defaultPageSize: DEFAULT_PAGE_SIZE,
        defaultHidden: defaultHiddenColumns,
        onResolved: () => setViewsReady(true),
    });

    return (
        <>
            <PageLayout.Header
                rightAddon={
                    hasRouteAccess('/console/$orgId/dashboard/complaints') && (
                        <Tooltip title="Dashboard">
                            <Button.Link
                                to="/console/$orgId/dashboard/complaints"
                                params={{ orgId }}
                                icon={<BarChartOutlined />}
                            />
                        </Tooltip>
                    )
                }
            />

            <TableViewsBar controller={views.controller} />

            <TableToolbar
                filters={
                    <FilterBar
                        config={filterConfig}
                        filters={views.filterState.filters}
                        whereClause={views.filterState.whereClause}
                        onAddFilter={views.filterState.addFilter}
                        onUpdateFilter={views.filterState.updateFilter}
                        onRemoveFilter={views.filterState.removeFilter}
                        onClearFilters={views.filterState.clearFilters}
                    />
                }
                actions={
                    <Space size="small">
                        {enableAiIntake && (
                            <Button.Link
                                to="/console/$orgId/compliance/intake"
                                params={{ orgId }}
                                variant="secondary"
                                beta
                            >
                                Smart intake
                            </Button.Link>
                        )}
                        <Button.Add.Link
                            to="/console/$orgId/compliance/complaints/add"
                            params={{ orgId }}
                            label="New complaint"
                            variant="primary"
                        />
                    </Space>
                }
            />

            {error ? (
                <InlineQueryError error={error} onRetry={() => refetch()} title="Could not load complaints" />
            ) : (
                <PrismaTable
                    {...tableProps}
                    expandable={{
                        expandedRowRender: (item) => (
                            <Row gutter={[16, 16]}>
                                <Col span={8}>
                                    <div className="text-muted-foreground">Flags</div>
                                    <ul>
                                        {item.Flags.map(({ id, labelActive }) => (
                                            <li key={id}>{labelActive}</li>
                                        ))}
                                    </ul>
                                </Col>
                                <Col span={8}>
                                    <div className="text-muted-foreground">Handling parties</div>
                                    <ul>
                                        {item.HandlingParties.map((el) => (
                                            <li key={el.handlingPartyId}>{el.HandlingParty?.name}</li>
                                        ))}
                                    </ul>
                                </Col>
                            </Row>
                        ),
                        rowExpandable: (item) => !!item.Flags.length || !!item.HandlingParties.length,
                    }}
                    onRow={({ id }) => ({
                        onClick: (e) => {
                            navigate(
                                {
                                    to: '/console/$orgId/compliance/complaints/details/$id/manage',
                                    params: { orgId, id },
                                },
                                e,
                            );
                        },
                        className: 'cursor-pointer',
                    })}
                />
            )}
        </>
    );
}
